// What the platform sells: each plan's list price, seats and monthly allowances, and the top-up
// packs. Platform staff change it on the Platform page (Pricing tab) without a deploy, so the
// marketing pages never hard-code a number:
//
//  - the build prerenders them with DEFAULT_PRICING (models.DefaultPricing on the server);
//  - the server sends every HTML page with the pricing in force as <script id="rhd-pricing">
//    and rewrites each element marked with priced(key) to pricingText(…)[key]
//    (server/routes/pricing_page.go), so the page is right before any script runs;
//  - in the browser pagePricing() reads that script, so hydration renders the same text.
//
// This module is in the marketing bundle: no app imports.
export const PRICED_PLANS = [
    'starter',
    'shop',
    'dealer'
];
export const DEFAULT_PRICING = {
    plans: {
        starter: {
            priceCents: 19900,
            seats: 1,
            sheets: 500,
            comps: 75
        },
        shop: {
            priceCents: 44900,
            seats: 3,
            sheets: 1500,
            comps: 150
        },
        dealer: {
            priceCents: 89900,
            seats: 0,
            sheets: 4000,
            comps: 300
        }
    },
    packs: {
        sheets: {
            count: 500,
            priceCents: 9900
        },
        comps: {
            count: 50,
            priceCents: 4900
        }
    },
    pass: {
        priceCents: 14900,
        days: 90,
        hunts: 3,
        sheets: 100,
        comps: 15,
        seats: 1
    },
    guarantee: {
        days: 90,
        claimDays: 30,
        sheetUnitCents: 20,
        compUnitCents: 98,
        payoutDays: 10
    },
    site: {
        monthlyCents: 14900,
        setupCents: 49900,
        dealerSetupCents: 0,
        edits: 10
    }
};
/** Reads the server's pricing JSON over the defaults (unknown or missing parts keep them). */ export function parsePricing(json) {
    const out = {
        plans: {
            ...DEFAULT_PRICING.plans
        },
        packs: {
            ...DEFAULT_PRICING.packs
        },
        pass: {
            ...DEFAULT_PRICING.pass
        },
        guarantee: {
            ...DEFAULT_PRICING.guarantee
        },
        site: {
            ...DEFAULT_PRICING.site
        }
    };
    let wire;
    try {
        wire = JSON.parse(json);
    } catch  {
        return out;
    }
    for (const id of PRICED_PLANS){
        const p = wire.plans?.[id];
        if (p) out.plans[id] = {
            priceCents: p.price_cents ?? 0,
            seats: p.seats ?? 0,
            sheets: p.sheets ?? 0,
            comps: p.comps ?? 0
        };
    }
    for (const kind of [
        'sheets',
        'comps'
    ]){
        const p = wire.packs?.[kind];
        if (p) out.packs[kind] = {
            count: p.count ?? 0,
            priceCents: p.price_cents ?? 0
        };
    }
    const ps = wire.pass;
    if (ps?.days) out.pass = {
        priceCents: ps.price_cents ?? 0,
        days: ps.days,
        hunts: ps.free_hunts || DEFAULT_PRICING.pass.hunts,
        sheets: ps.sheets ?? 0,
        comps: ps.comps ?? 0,
        seats: ps.seats ?? 0
    };
    const g = wire.guarantee;
    if (g?.days) out.guarantee = {
        days: g.days,
        claimDays: g.claim_days ?? 0,
        sheetUnitCents: g.sheet_unit_cents ?? 0,
        compUnitCents: g.comp_unit_cents ?? 0,
        payoutDays: g.payout_days ?? 0
    };
    const st = wire.site;
    if (st?.monthly_cents) out.site = {
        monthlyCents: st.monthly_cents,
        setupCents: st.setup_cents ?? 0,
        dealerSetupCents: st.dealer_setup_cents ?? 0,
        edits: st.edits ?? 0
    };
    return out;
}
let cached = null;
/**
 * The pricing this page was served with: the server's script in the browser, the defaults
 * while prerendering (and in dev, where webpack serves the page). The same on the server and
 * on first client render, since the server wrote the markup from the same pricing.
 */ export function pagePricing() {
    if (typeof document === 'undefined') return DEFAULT_PRICING;
    const json = document.getElementById('rhd-pricing')?.textContent ?? '';
    if (!json) return DEFAULT_PRICING;
    if (cached?.json !== json) cached = {
        json,
        pricing: parsePricing(json)
    };
    return cached.pricing;
}
const thousands = (n)=>n.toLocaleString('en-US');
/** "$449", "$1,299.50" — cents only when there are any (services.FormatCents). */ export function formatCents(cents) {
    const dollars = thousands(Math.trunc(cents / 100));
    const rest = cents % 100;
    return rest === 0 ? `$${dollars}` : `$${dollars}.${String(rest).padStart(2, '0')}`;
}
/**
 * A per-unit price, cents ÷ per as dollars to at most three decimals: "$0.198", "$0.98", "$1"
 * (services.UnitPriceText).
 */ export function unitPriceText(cents, per) {
    if (per <= 0) return '$0';
    const mills = Math.floor((20 * cents + per) / (2 * per));
    if (mills % 10 === 0) return formatCents(mills / 10);
    return `$${Math.floor(mills / 1000)}.${String(mills % 1000).padStart(3, '0')}`;
}
/** The retail per-unit prices usage is charged at under the guarantee (services.GuaranteeRates). */ export function guaranteeRates(p) {
    const rate = (pk, fallback)=>pk.count > 0 && pk.priceCents > 0 ? {
            cents: pk.priceCents,
            per: pk.count,
            source: 'pack'
        } : {
            cents: fallback,
            per: 1,
            source: 'fallback'
        };
    return {
        sheet: rate(p.packs.sheets, p.guarantee.sheetUnitCents),
        comp: rate(p.packs.comps, p.guarantee.compUnitCents)
    };
}
/** The lowest plan price ("plans from $199"). */ export function fromCents(p) {
    const prices = PRICED_PLANS.map((id)=>p.plans[id].priceCents).filter((c)=>c > 0);
    return prices.length ? Math.min(...prices) : 0;
}
export function pricingText(p) {
    const count = (n)=>n === 0 ? 'Unlimited' : thousands(n);
    const users = (n)=>n === 0 ? 'Unlimited users' : n === 1 ? '1 user' : `${thousands(n)} users`;
    const out = {
        from: formatCents(fromCents(p))
    };
    for (const id of PRICED_PLANS){
        const pp = p.plans[id];
        out[`plan.${id}.price`] = formatCents(pp.priceCents);
        out[`plan.${id}.annual`] = formatCents(pp.priceCents * 10);
        out[`plan.${id}.sheets`] = count(pp.sheets);
        out[`plan.${id}.comps`] = count(pp.comps);
        out[`plan.${id}.users`] = users(pp.seats);
    }
    for (const kind of [
        'sheets',
        'comps'
    ]){
        out[`pack.${kind}.count`] = thousands(p.packs[kind].count);
        out[`pack.${kind}.price`] = formatCents(p.packs[kind].priceCents);
    }
    out['pass.price'] = formatCents(p.pass.priceCents);
    out['pass.days'] = thousands(p.pass.days);
    out['pass.hunts'] = p.pass.hunts === 1 ? '1 hunt' : `Up to ${thousands(p.pass.hunts)} hunts`;
    out['pass.sheets'] = thousands(p.pass.sheets);
    out['pass.comps'] = thousands(p.pass.comps);
    out['guarantee.days'] = thousands(p.guarantee.days);
    out['guarantee.claim'] = thousands(p.guarantee.claimDays);
    const rates = guaranteeRates(p);
    out['guarantee.sheet'] = unitPriceText(rates.sheet.cents, rates.sheet.per);
    out['guarantee.comp'] = unitPriceText(rates.comp.cents, rates.comp.per);
    const st = p.site;
    out['site.monthly'] = formatCents(st.monthlyCents);
    out['site.annual'] = formatCents(st.monthlyCents * 10);
    out['site.setup'] = formatCents(st.setupCents);
    out['site.dealer'] = st.dealerSetupCents === 0 ? 'Free' : formatCents(st.dealerSetupCents);
    out['site.edits'] = thousands(st.edits);
    return out;
}
/** Marks an element whose whole text is key's, for the server to rewrite. */ export function priced(key) {
    return {
        'data-price': key
    };
}
